// CRM: Mailentwurf per KI. Der Nutzer sagt in einem Satz, was drin stehen soll —
// den Rest (an wen, in welcher Sprache, worauf bezogen) liefert das Compose-Fenster
// aus dem Kontext, den es ohnehin schon hat.
//
// Eigenes Modul und nicht im Fenster: der Prompt ist die eigentliche Arbeit hier und
// laesst sich so ohne DOM pruefen. Die Anrede-Werte kommen aus emailPlaceholders —
// dieselbe Quelle wie bei den Vorlagen, damit die KI nicht anders gruesst als ein
// Baustein.
import { BedrockHelper, parseAIJson } from "./BedrockHelper";
import { GENERAL_MODEL } from "../constants/aiModels";
import { languages } from "../constants/languages";

const LANGUAGE_MAP = languages.reduce((acc, { languageCode, languageName }) => {
  acc[languageCode] = languageName;
  return acc;
}, {});

/**
 * Ein unterstuetzter Sprachcode, oder null.
 *
 * Gebraucht wird das, weil die Kommunikationssprache aus drei Quellen kommt, von denen
 * KEINE garantiert einen sauberen Code liefert: aus der Datenbank kommt auch `" "` —
 * das Sentinel dieses Projekts fuer "leer" —, aus der Spracherkennung auch `"fr-FR"`.
 * Beides ist wahr genug, um die naechste Quelle zu verdecken, und landete frueher
 * still auf Deutsch. Wer die Sprache waehlt, prueft sie deshalb HIER, nicht mit `||`.
 */
export const normalizeLanguage = (value) => {
  const code = String(value ?? "").trim().toLowerCase().split(/[-_]/)[0];
  return LANGUAGE_MAP[code] ? code : null;
};

// Bezugstexte (Vorgaengermail, Aufgabe, eigener Entwurf) gekuerzt: der Anfang traegt
// das Anliegen, der Rest sind Zitate und Signaturen.
const MAX_CONTEXT_CHARS = 1500;

const clip = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_CONTEXT_CHARS);

const block = (label, value) => (clip(value) ? `${label}: ${clip(value)}` : "");

/**
 * @param instruction  was der Nutzer eingetippt hat ("Termin fuer naechste Woche vorschlagen")
 * @param values       Ergebnis von placeholderValues({ company, contact, sender })
 * @param language     Sprachcode der Mail (Kommunikationssprache des Kontakts)
 * @param subject      aktueller Betreff, falls schon einer dasteht
 * @param draft        aktueller Text als Klartext — die KI ueberarbeitet ihn dann
 * @param replyTo      { subject, description } der Mail, auf die geantwortet wird
 * @param task         { subject, body } der Aufgabe, aus der die Mail entsteht
 */
export const buildEmailPrompt = ({
  instruction,
  values = {},
  language,
  subject,
  draft,
  replyTo,
  task,
} = {}) => {
  const targetLanguage = LANGUAGE_MAP[normalizeLanguage(language)] || LANGUAGE_MAP.en;
  const context = [
    block("Customer company", values.companyName),
    block("Recipient", [values.contactFirstName, values.contactLastName].filter(Boolean).join(" ")),
    // Die Anrede ist der Punkt, an dem eine Mail persoenlich oder falsch wird: nur
    // die geprueften Formen aus emailPlaceholders gehen rein, geraten wird nichts.
    //
    // Sie kommt IMMER auf Deutsch ("Sehr geehrte Frau Dr. Mueller") — emailPlaceholders
    // kennt nur diese Form. Uebersetzt wird sie deshalb hier, statt in vier Sprachen
    // ausformuliert zu werden: welche Anrede im Chinesischen vor und welche hinter dem
    // Namen steht, weiss das Modell besser als eine Tabelle. Wer (Herr/Frau) und welcher
    // Titel gemeint ist, steht fest — nur die Sprache darf sich aendern.
    block(
      "Greeting line to use — translate it into the target language if it is in another one, keeping the person's name and any academic title (Dr., Prof.) unchanged",
      values.personalGreeting || values.formalGreeting
    ),
    block("Sender", [values.senderFirstName, values.senderLastName].filter(Boolean).join(" ")),
    block("Current subject", subject),
    block("Previous email being answered — subject", replyTo?.subject),
    block("Previous email being answered — text", replyTo?.description),
    block("Task this email belongs to", [task?.subject, task?.body].filter(Boolean).join(" — ")),
    block("Existing draft to rework", draft),
  ]
    .filter(Boolean)
    .join("\n");

  return `You are writing a business email for a salesperson at a B2B trade company.

<context>
${context || "no additional context"}
</context>

<instruction>
${clip(instruction)}
</instruction>

Rules:
- Follow the instruction. Use the context only for facts and tone; never invent prices, dates, quantities, names or agreements that are not given.
- Write in ${targetLanguage}.
- Start with the greeting line from the context, rendered in ${targetLanguage} — keep the person's name and any academic title exactly, translate only the form of address. If no greeting line is given, use a neutral greeting appropriate to ${targetLanguage}.
- End with the last sentence of your message. Do NOT add a closing phrase ("Mit freundlichen Grüßen", "Kind regards", …), the sender name, job title, phone number or any signature — the signature already carries all of that and is appended automatically.
- Plain text, short paragraphs separated by a blank line. No markdown, no HTML, no bullet characters unless the instruction asks for a list.
- Never output template placeholders such as {{name}} or [[name]]. If a fact is missing, leave it out or ask for it in a sentence.
- Keep it short: a business email, not an essay.
- Return ONLY a JSON object: {"subject":"...","body":"..."} with line breaks in body written as \n.`;
};

/** @returns {Promise<{subject: string, body: string}>} */
export const generateEmailDraft = async (context) => {
  const response = await BedrockHelper(buildEmailPrompt(context), GENERAL_MODEL);
  const parsed = parseAIJson(response);
  const body = String(parsed?.body ?? "").trim();
  if (!body) throw new Error("empty draft");
  return { subject: String(parsed?.subject ?? "").trim(), body };
};
